import React, { forwardRef, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import CommentCard from './CommentCard';

const EssayColumn = forwardRef(({
    text,
    highlights,
    activeComment,
    hoveredComment,
    onHighlightClick,
    onHighlightHover,
    onHighlightLeave,
    colors,
    isMobile,
    feedbackJson,
}, ref) => {
    // Build highlighted text content
    const renderedContent = useMemo(() => {
        if (!text) return null;
        if (!highlights || highlights.length === 0) {
            return <span>{text}</span>;
        }

        // Sort highlights by start index
        const sortedHighlights = [...highlights].sort((a, b) => a.startIndex - b.startIndex);

        const elements = [];
        let lastIndex = 0;

        sortedHighlights.forEach((highlight, idx) => {
            // Add text before this highlight
            if (highlight.startIndex > lastIndex) {
                elements.push(
                    <span key={`text-${idx}`}>
                        {text.slice(lastIndex, highlight.startIndex)}
                    </span>
                );
            }

            // Add highlighted text
            const isActive = activeComment === highlight.id;
            const isHovered = hoveredComment === highlight.id;
            const highlightClass = `highlight highlight--${highlight.type} ${isActive ? 'active' : ''} ${isHovered ? 'hovered' : ''}`;

            elements.push(
                <span
                    key={`highlight-${idx}`}
                    id={`highlight-${highlight.id}`}
                    className={highlightClass}
                    onClick={() => onHighlightClick(highlight.id)}
                    onMouseEnter={() => onHighlightHover && onHighlightHover(highlight.id)}
                    onMouseLeave={() => onHighlightLeave && onHighlightLeave()}
                    style={{ cursor: 'pointer' }}
                >
                    {text.slice(highlight.startIndex, highlight.endIndex)}
                </span>
            );

            // For mobile: show inline comment when active
            if (isMobile && isActive) {
                elements.push(
                    <Box
                        key={`mobile-comment-${idx}`}
                        className="mobile-inline-comment"
                    >
                        <CommentCard
                            item={highlight.data}
                            type={highlight.type}
                            isActive={true}
                            onClick={() => {}}
                            colors={colors}
                            compact={true}
                        />
                    </Box>
                );
            }

            lastIndex = highlight.endIndex;
        });

        // Add remaining text after last highlight
        if (lastIndex < text.length) {
            elements.push(
                <span key="text-end">
                    {text.slice(lastIndex)}
                </span>
            );
        }

        return elements;
    }, [text, highlights, activeComment, hoveredComment, onHighlightClick, onHighlightHover, onHighlightLeave, colors, isMobile]);

    return (
        <Paper
            ref={ref}
            elevation={2}
            sx={{
                p: 3,
                backgroundColor: colors.primary[700],
                borderRadius: '12px',
                minHeight: '400px',
            }}
        >
            <Typography variant="h6" fontWeight="bold" mb={2} color={colors.grey[300]}>
                Your Essay
            </Typography>
            <Box className="essay-content">
                {renderedContent}
            </Box>

            {/* Mobile: Show all feedback summary at the bottom */}
            {isMobile && feedbackJson && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2} color={colors.blueAccent[400]}>
                        Strengths ({feedbackJson.strengths?.length || 0})
                    </Typography>
                    {feedbackJson.strengths?.map((strength, idx) => (
                        <Box key={`strength-${idx}`} sx={{ mb: 2 }}>
                            <CommentCard
                                item={{ ...strength, id: `strength-${idx}` }}
                                type="strength"
                                isActive={false}
                                onClick={() => {}}
                                colors={colors}
                            />
                        </Box>
                    ))}

                    <Typography variant="h6" fontWeight="bold" mb={2} mt={3} color={colors.redAccent[400]}>
                        Areas for Improvement ({feedbackJson.improvements?.length || 0})
                    </Typography>
                    {feedbackJson.improvements?.map((improvement, idx) => (
                        <Box key={`improvement-${idx}`} sx={{ mb: 2 }}>
                            <CommentCard
                                item={{ ...improvement, id: `improvement-${idx}` }}
                                type="improvement"
                                isActive={false}
                                onClick={() => {}}
                                colors={colors}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
});

EssayColumn.displayName = 'EssayColumn';

export default EssayColumn;
